import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form'

import { Banner } from '@/components/ui/banner'
import { YesNo } from '@/components/ui/button-group'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Driver } from '../../../../models/driver'
import { driverOption, PrefilledField, RELATIONSHIP_OPTIONS } from './ClaimStep2Fields'
import { NOT_LISTED, type Step2FormValues } from './claimStep2Values'

const DRIVER_NOT_LISTED = { value: NOT_LISTED, label: 'Driver not on this list' }

interface ClaimDriverSectionProps {
  control: Control<Step2FormValues>
  register: UseFormRegister<Step2FormValues>
  setValue: UseFormSetValue<Step2FormValues>
  errors: FieldErrors<Step2FormValues>
  drivers: Driver[]
}

export function ClaimDriverSection({
  control,
  register,
  setValue,
  errors,
  drivers,
}: ClaimDriverSectionProps) {
  const driverSelection = useWatch({ control, name: 'driverSelection' })
  const driverManual = driverSelection === NOT_LISTED

  /** The picked driver's license number, or empty boxes for a driver not on the roster. */
  function seed(driver: Driver | undefined) {
    setValue('driverName', '')
    setValue('driverCdl', driver?.licenseNumber ?? '')
  }

  return (
    <>
      <Controller
        name="driverSelection"
        control={control}
        rules={{ required: 'Select the driver.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700" htmlFor="claim-driver">
              Who was driving? *
            </label>
            <SearchableSelect
              id="claim-driver"
              options={drivers.map(driverOption)}
              value={field.value || undefined}
              onChange={(next) => {
                // On the pick itself, so the roster arriving never overwrites a saved claim.
                if (next !== field.value) seed(drivers.find((d) => d.id === next))
                field.onChange(next)
              }}
              footerOption={DRIVER_NOT_LISTED}
              placeholder="— Select driver —"
              searchPlaceholder="Search name or license number"
              emptyMessage="No driver matches that"
              aria-invalid={fieldState.error ? true : undefined}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      {driverManual && (
        <>
          <Banner variant="warning" small>
            This driver is not on your roster. Please enter their details manually. Your broker will follow up
            to add them to your policy if needed.
          </Banner>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">Driver name *</label>
            <Input
              placeholder="e.g. Jane Smith"
              aria-invalid={errors.driverName ? true : undefined}
              {...register('driverName', {
                validate: (val, f) =>
                  f.driverSelection !== NOT_LISTED || val.trim() !== '' || 'Driver name is required.',
              })}
            />
            <FieldError message={errors.driverName?.message} />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {driverManual ? (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">CDL number</label>
            <Input placeholder="CDL number" {...register('driverCdl')} />
          </div>
        ) : driverSelection ? (
          <PrefilledField label="CDL number" {...register('driverCdl')} />
        ) : null}
        <Controller
          name="relationship"
          control={control}
          rules={{ required: 'Please pick a relationship.' }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Relationship to insured *</label>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger className="w-full" aria-invalid={fieldState.error ? true : undefined}>
                  <SelectValue placeholder="— Select —" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      <Controller
        name="wasOnDuty"
        control={control}
        rules={{ validate: (val) => val !== null || 'Please choose.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">Was driver on duty? *</label>
            <YesNo value={field.value} onChange={field.onChange} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />
    </>
  )
}
